import { AxiosProgressEvent } from 'axios';
import { Box, Grid, Typography, Link, Input, Stack, IconButton } from '@mui/material';
import { IComponentCss, UseFetchDataState, useFetchData } from '@tap/mui-ui';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import { ApiAttachmentExt, attachmentService, issueService } from '@tap/data-providers';
import React, { useRef, useState } from 'react';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { dateTimeHelper } from '@tap/utils';

{/* Вложенные файлы */ }

export type AttachmentsBlockProps = {
    documentKey: string,
    attachmentsState: UseFetchDataState<ApiAttachmentExt[]>,
    uploadInputRef: React.MutableRefObject<HTMLInputElement | undefined>,
    hundleOpenSelectFile: (dataProperty?: string) => void,
    fileUploadCallback?: () => void,
}

export enum LoadingStatus {
    default = 'default',
    loading = 'loading',
    imported = 'imported',
    error = 'error',
}


const css: IComponentCss = {
    inputFile: {
        display: 'block',
        border: 'none',
        textTransform: 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        opacity: 0,

        '&:focus': {
            outline: 'none',
        },
    },
    linkButton: {

    },
    attachmentBox: {
        ':hover': {
            background: '#F5F5F5',
            ' .delete-button': {
                display: 'inline-flex',
            },
        },
    },
    thumb: {
        width: '200px',
        height: '145px',
        border: '1px solid #e9e9e9',
        borderRadius: '3px',
        display: 'table-cell',
        textAlign: 'center',
        verticalAlign: 'middle',
    },
    imgBox: {
        maxWidth: '200px',
        maxHeight: '145px',
        verticalAlign: 'text-top',
        fontSize: '32px',
    },
    linkText: { textAlign: 'center', minHeight: '42px' },
    fileDesc: {
        margin: '5px 10px',
        width: '180px',
        overflow: 'hidden',
    },
    fileName: {
        width: '180px',
        whiteSpace: 'nowrap',
    },
    fileDate: {
        color: '#999999',
        fontSize: '12px',
    },
    fileSize: {
        color: '#999999',
        fontSize: '12px',
    },
    deleteBg: {
        position: 'absolute',
        top: 0,
        right: 0,
        backgroundSize: '100% 100%',
        backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0) 0, #fff 100%)',
        pointerEvents: 'none',
        width: '60px',
        height: '20px',
    },
    fileNameBox: {
        position: 'relative',
    },
    deleteButton: {
        position: 'absolute',
        bottom: '-4px',
        right: '-8px',
        cursor: 'pointer',
        display: 'none',
    },
}

export type UseAttachmentsProps = {
    documentKey: string,
    fileUploadCallback?: () => void,
}

export const useAttachments = ({
    documentKey,
    fileUploadCallback,
}: UseAttachmentsProps): AttachmentsBlockProps => {
    const [attachmentsState] = useFetchData(() => {
        return attachmentService.getAttachmentList(documentKey);
    }, [documentKey]);

    const uploadInputRef = useRef<HTMLInputElement | undefined>(undefined);
    const hundleOpenSelectFile = (dataProperty?: string) => {
        if (uploadInputRef?.current?.children?.[0]) {
            const fileInputEl = uploadInputRef.current.children[0] as HTMLInputElement;
            if (dataProperty) {
                fileInputEl.setAttribute('dataProperty', dataProperty);
            }
            fileInputEl.click();
        }
    }

    const AttachmentsJsx = <>
        <AttachmentsBlock
            documentKey={documentKey}
            attachmentsState={attachmentsState}
            uploadInputRef={uploadInputRef}
            hundleOpenSelectFile={hundleOpenSelectFile}
            fileUploadCallback={fileUploadCallback}
        />
    </>;

    return { documentKey, AttachmentsJsx, attachmentsState, hundleOpenSelectFile, uploadInputRef, fileUploadCallback };
}

export const AttachmentsBlock = ({
    documentKey,
    attachmentsState,
    uploadInputRef,
    hundleOpenSelectFile,
    fileUploadCallback,
}: AttachmentsBlockProps) => {
    const [isDragOver, setIsDragOver] = useState(false);

    const selectFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileInputEl = event.target as HTMLInputElement;
        const dataProperty = fileInputEl.getAttribute('dataProperty');
        fileInputEl.setAttribute('dataProperty', undefined);
        const files: File[] = Array.from(event.target.files || []);
        attachmentsState.setData((prev) => {

            const newFiles: ApiAttachmentExt[] = files.map(f => ({
                isNew: true,
                id: 0,
                uid: '',
                authorId: 0,
                createdAtUtc: new Date(),
                documentKey,
                originalName: f.name,
                filePath: URL.createObjectURL(f),
                isImage: f.type.indexOf('image') !== -1,
                data: {
                    type: f.type,
                    size: f.size,
                },
            }))
            return [...newFiles, ...(prev || [])]

        });

        await Promise.all(files.map(f => attachmentService.upload(documentKey, [f], onProgressChange)
            .then((res) => {
                attachmentsState.setData((prev) => {
                    res.rows.forEach(serverFile => {
                        const fileItem = prev.find(f => f.isNew && f.originalName === serverFile.originalName);
                        if (fileItem) {
                            fileItem.isNew = false;
                            fileItem.id = serverFile.id;
                            fileItem.uid = serverFile.uid;
                        }
                    });
                    return prev;
                });

                if (dataProperty) {
                    return issueService.updateIssue(documentKey, { data: { [dataProperty]: res } });
                }
            })));

        fileUploadCallback && fileUploadCallback();
    }

    const onProgressChange = (_event: AxiosProgressEvent) => {
        //console.log(Math.round(100 * event.loaded / (event.total || event.loaded)));
    };

    const handleDelete = async (file: ApiAttachmentExt) => {
        await attachmentService.deleteAttachment(file.uid);
        attachmentsState.setData((prevValue) => prevValue?.filter(f => f.uid !== file.uid));
    }

    return (
        <Grid
            container
            onDragOver={(_) => setIsDragOver(true)}
            onDragLeave={(_) => setIsDragOver(false)}
            onDrop={(_) => setIsDragOver(false)}
            sx={{
                position: 'relative',
                border: '1px dashed #E0E0E0',
                minHeight: '140px',
                marginTop: '6px',
                padding: '16px',
                borderColor: isDragOver ? '#1976d2' : '#E0E0E0',
                backgroundColor: isDragOver ? '#fafafa' : '#fff',
            }}>
            <Grid
                container
                justifyContent='center'
                alignItems='center'
            >
                <Input
                    ref={uploadInputRef}
                    type='file'
                    title=''
                    inputProps={{
                        multiple: true,
                        accept: '*',
                    }}
                    sx={{
                        ...css.inputFile,
                        width: isDragOver ? '100%' : undefined,
                        height: isDragOver ? '100%' : undefined,
                        '& .MuiInputBase-input': {
                            width: isDragOver ? '100%' : undefined,
                            height: isDragOver ? '100%' : undefined,
                        },
                    }}
                    onChange={selectFiles} />
                <BackupOutlinedIcon sx={{ marginRight: '10px' }} />
                <Typography>
                    Перенесите файлы, чтобы прикрепить, или
                    <Typography
                        component={Link}
                        onClick={() => hundleOpenSelectFile()}
                        sx={{ fontWeight: 700, fontSize: '14px', marginLeft: '3px' }}
                    >
                        обзор
                    </Typography>
                    .
                </Typography>
                <Grid container
                    spacing={2}
                    mt={1}>
                    {(attachmentsState?.data || []).map(f => (
                        <Grid item key={f.uid || f.originalName}>
                            <Box sx={css.attachmentBox}>
                                <Box sx={css.thumb}>
                                    <Link
                                        color='primary'
                                        href={f.filePath}
                                        target='_blank'
                                        rel='noreferrer'
                                        sx={css.linkButton}
                                    >
                                        {f.isImage && (
                                            <Box
                                                component='img'
                                                src={f.filePath}
                                                sx={css.imgBox}
                                                alt={f.originalName}
                                            />
                                        )}

                                        {!f.isImage && (
                                            <InsertDriveFileOutlinedIcon
                                                sx={css.imgBox}
                                                fontSize='large'
                                            />
                                        )}
                                    </Link>
                                </Box>

                                <Box component='dl' sx={css.fileDesc}>
                                    <Box component='dt' sx={css.fileNameBox}>
                                        <Box component='span'
                                            sx={css.deleteBg}
                                            className='delete-bg'></Box>

                                        <IconButton
                                            size='small'
                                            sx={css.deleteButton}
                                            className='delete-button'
                                            onClick={() => handleDelete(f)}
                                        >
                                            <DeleteForeverIcon fontSize='small' />
                                        </IconButton>


                                        <Typography
                                            variant='body1'
                                            sx={css.fileName}>
                                            {f.originalName}
                                        </Typography>
                                    </Box>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        spacing={0}
                                    >
                                        <Typography component='dd'
                                            variant='body1'
                                            sx={css.fileDate}>
                                            {dateTimeHelper.formatDate(new Date(f.createdAtUtc), 'dd MMM yy HH:mm')}
                                        </Typography>
                                        <Typography component='dd'
                                            variant='body1'
                                            sx={css.fileSize}>
                                            {`${Math.floor(f.data.size / 1024 / 8)} Kb`}
                                        </Typography>
                                    </Stack>
                                </Box>


                            </Box>

                        </Grid>

                    ))}
                </Grid>
            </Grid>
        </Grid >
    )
}
